import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { setToken, setUser } from "../lib/auth";
import { toast } from "sonner";
import { Loader2, MonitorDot, ShieldCheck, Smartphone } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const AdminAuthCallback = () => {
  const nav = useNavigate();
  const hasProcessed = useRef(false);
  const [stage, setStage] = useState("exchanging"); // 'exchanging' | '2fa' | 'verifying'
  const [challenge, setChallenge] = useState(null); // {challenge_token, email, name, masked_phone, expires_in}
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      nav("/admin/login");
      return;
    }
    const session_id = decodeURIComponent(match[1]);
    // Clean the hash early so a refresh doesn't replay the OAuth call
    window.history.replaceState(null, "", "/admin");

    (async () => {
      try {
        const res = await api.post("/admin/auth/session", { session_id });
        if (res.data?.pending_2fa) {
          setChallenge({
            challenge_token: res.data.challenge_token,
            email: res.data.email,
            name: res.data.name,
            picture: res.data.picture,
            masked_phone: res.data.masked_phone,
            expires_in: res.data.expires_in || 300,
          });
          setSecondsLeft(res.data.expires_in || 300);
          setStage("2fa");
          toast.success("Étape 2 : code SMS envoyé sur le téléphone Admin.");
          return;
        }
        // Legacy single-step path (kept as safety net)
        if (res.data?.session_token) setToken(res.data.session_token);
        setUser({ email: res.data.email, name: res.data.name, picture: res.data.picture, role: "admin" });
        toast.success(`Bienvenue ${res.data.name || res.data.email}`);
        nav("/admin", { replace: true });
      } catch (e) {
        const msg = e?.response?.data?.detail || "Connexion Google échouée";
        toast.error(msg);
        nav("/admin/login", { replace: true });
      }
    })();
  }, [nav]);

  // OTP expiry countdown
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft]);

  const fmtSeconds = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const verify = async (e) => {
    e?.preventDefault?.();
    if (!/^\d{6}$/.test(code)) {
      toast.error("Le code doit comporter 6 chiffres");
      return;
    }
    if (!challenge?.challenge_token) return;
    setVerifying(true);
    try {
      const res = await api.post("/admin/auth/google/verify-2fa", {
        challenge_token: challenge.challenge_token,
        code,
      });
      if (res.data?.session_token) setToken(res.data.session_token);
      setUser({
        email: res.data.email,
        name: res.data.name,
        picture: res.data.picture,
        role: "admin",
        method: "google+sms_2fa",
      });
      toast.success(`Bienvenue ${res.data.name || res.data.email}`);
      nav("/admin", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Code incorrect";
      toast.error(typeof msg === "string" ? msg : "Code incorrect");
    } finally {
      setVerifying(false);
    }
  };

  const resend = async () => {
    if (!challenge?.challenge_token) return;
    setResending(true);
    try {
      const res = await api.post("/admin/auth/google/resend-2fa", {
        challenge_token: challenge.challenge_token,
      });
      setSecondsLeft(res?.data?.expires_in || 300);
      setCode("");
      toast.success("Nouveau code SMS envoyé.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Renvoi impossible");
    } finally {
      setResending(false);
    }
  };

  // --- Stage: SMS 2FA challenge ---
  if (stage === "2fa" && challenge) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-5 py-12" data-testid="admin-2fa-page">
        <form onSubmit={verify} className="w-full max-w-md">
          <div className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <MonitorDot className="w-5 h-5 text-white" strokeWidth={2.25} />
            </div>
            <span className="font-bold text-xl tracking-tight text-white">
              Le Guide des Entrepreneurs
            </span>
          </div>

          <div className="flex items-center gap-2 mt-10 text-xs font-bold uppercase tracking-widest text-emerald-400">
            <ShieldCheck className="w-3.5 h-3.5" /> Étape 2 / 2 — Vérification SMS
          </div>
          <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tighter mt-3 mb-2">
            Double authentification
          </h1>
          <p className="text-white/70 text-sm mb-2">
            Connexion Google validée pour <strong className="text-white">{challenge.email}</strong>.
          </p>
          <p className="text-white/60 text-sm mb-6">
            Un code à 6 chiffres a été envoyé par SMS au numéro Admin{" "}
            <strong className="text-white">{challenge.masked_phone}</strong> via Free Mobile.
          </p>

          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-white/60 mb-1.5 block">
              Code reçu par SMS
            </Label>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              autoFocus
              required
              placeholder="••••••"
              className="rounded-none bg-white/5 border-white/20 text-white placeholder:text-white/30 text-center text-2xl font-mono tracking-[0.5em] py-6"
              data-testid="admin-2fa-code"
            />
            <div className="flex items-center justify-between mt-2 text-xs text-white/50">
              <span className="inline-flex items-center gap-1.5">
                <Smartphone className="w-3 h-3" />
                Free Mobile
              </span>
              <span>{secondsLeft > 0 ? `Valide ${fmtSeconds(secondsLeft)}` : "Expiré"}</span>
            </div>
          </div>

          <Button
            type="submit"
            disabled={verifying || code.length !== 6}
            className="w-full mt-6 rounded-none bg-emerald-500 hover:bg-emerald-600 text-black py-6 text-base font-semibold disabled:opacity-50"
            data-testid="admin-2fa-submit"
          >
            {verifying ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Vérification…
              </>
            ) : (
              "Valider et accéder au CMS"
            )}
          </Button>

          <div className="mt-4 flex items-center justify-between text-xs">
            <button
              type="button"
              onClick={resend}
              disabled={resending || secondsLeft > 240}
              className="text-emerald-400 hover:underline disabled:opacity-40 disabled:no-underline"
              data-testid="admin-2fa-resend"
            >
              {resending ? "Renvoi…" : "Renvoyer un code SMS"}
            </button>
            <button
              type="button"
              onClick={() => nav("/admin/login", { replace: true })}
              className="text-white/60 hover:text-white"
              data-testid="admin-2fa-cancel"
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    );
  }

  // --- Stage: exchanging OAuth code ---
  return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center gap-4 px-5" data-testid="admin-auth-callback">
      <div className="flex items-center gap-2" data-testid="logo-link">
        <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
          <MonitorDot className="w-5 h-5 text-white" strokeWidth={2.25} />
        </div>
        <span className="font-bold text-xl tracking-tight text-white">
          Le Guide des Entrepreneurs
        </span>
      </div>
      <Loader2 className="w-8 h-8 animate-spin text-orange-400 mt-2" />
      <p className="text-sm text-white/70 uppercase tracking-widest">Connexion à l'espace admin…</p>
    </div>
  );
};

export default AdminAuthCallback;
